import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { AnnouncementAction, AnnouncementGetter } from '@/store/announcement/announcement.module';
import { AnnouncementAttachmentSchema } from '@/store/announcement/announcement.validator';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'AnnouncementUploadComponent',
  components: {
    BaseModalComponent,
  },
  props: {
    announcementId: {
      type: Number,
    },
  },
  emits: [
    'hidden'
  ],
  setup: (props, context) => {
    const store = useStore();
    const announcementForm = ref<InstanceType<typeof BaseModalComponent>>();
    const fileDialog = ref<HTMLInputElement>();

    // form
    interface Form {
      announcementId?: number;
      name: string;
      file: string;
    }
    const formData = reactive<Form>({
      announcementId: props.announcementId,
      name: '',
      file: '',
    });
    const validator = computed(() => AnnouncementAttachmentSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      file: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'file')),
    });
    const isValid = computed(() => !errors.name && !errors.file);
    const isLoading = computed(() => store.getters[AnnouncementGetter.STATUS] === 'init' || store.getters[AnnouncementGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show
    const show = () => {
      Promise.resolve(formData.announcementId = props.announcementId)
        .then(() => announcementForm.value?.show());
    };

    // hide
    const hide = () => {
      announcementForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.name = '';
        formData.file = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        const files = fileDialog.value?.files as FileList;
        store.dispatch(AnnouncementAction.ADD_ATTACHMENT, {
          announcementId: formData.announcementId,
          attachmentTypeId: 1,
          names: [formData.name],
          files: files,
        }).then(() => hide());
      }
    };

    const fileDialogChange = () => {
      const files = fileDialog.value?.files as FileList;
      const file = files.item(0) as File;
      formData.file = file.name;
      unsubmit();
    };

    const handleHidden = () => {
      context.emit('hidden');
    };

    return {
      formData,
      announcementForm,
      fileDialog,
      isLoading,
      errors,
      submited,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
      fileDialogChange,
      handleHidden,
    };
  }
});
